import React from 'react';
import { useRoutes, Outlet, Navigate } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import RouteGuard from '../../components/common/route-guard';
import SmartRedirect from '../../components/common/smart-redirect';
import UsersLoginPageComponent from '../users/login';
import CompaniesListPageComponent from '../companies/list';
import LayoutHeadingComponent from '../../components/layout/heading';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import NotFoundPageComponent from '../text/not-found';
import AccessDeniedPageComponent from '../access-denied/access-denied';
import CompaniesViewPageComponent from '../companies/view';
import UsersEditPageComponent from '../users/edit';
import UsersListPageComponent from '../users/list';
import EmployeesListPageComponent from '../employees/list';
import EmployeesEditPageComponent from '../employees/edit';
import BadgesListPageComponent from '../badges/list';
import BadgesEditPageComponent from '../badges/edit';
import TranslationsListPageComponent from '../translations/list';
import TranslationsEditPageComponent from '../translations/edit';
import AlbumsViewPageComponent from '../albums/view';
import AlbumsPageComponent from '../albums/list';
import PhotosPageComponent from '../photos/local/list';
import PhotosUploadPageComponent from '../photos/local/upload';
import PhotosEditPageComponent from '../photos/local/edit';
import PhotosTranslatePageComponent from '../photos/local/translate';
import PhotosForeignPageComponent from '../photos/foreign/list';
import AlbumsUploadPageComponent from '../albums/upload';
import ReportsUserContentPageComponent from '../reports/user-content';
import ReportsUsagePageComponent from '../reports/usage-report';
import PressreleaseStatsReportPageComponent from '../reports/pressrelease-stats';
import ReportsLogsPageComponent from '../reports/logs';
import CategoriesListPageComponent from '../categories/list';
import CategoriesEditPageComponent from '../categories/edit';
import EasyToReadGeneratorPageComponent from '../easy-2-read/generator';
import VideosPageComponent from '../videos/list';
import VideoViewPageComponent from '../videos/view';

export default function Root() {
  const { user, loading } = useAuth();

  const isLoggedIn = !loading && user;

  const element = useRoutes([
    {
      path: '/',
      element: !isLoggedIn ? <Navigate to="/login" /> : <Outlet />,
      children: [
        {
          path: '/',
          element: <SmartRedirect />,
        },
        // {
        //   path: 'reports',
        //   element: (
        //     <RouteGuard permission="view activity log" redirectTo="/access-denied">
        //       <Outlet />
        //     </RouteGuard>
        //   ),
        //   children: [
        //     {
        //       path: 'user-content',
        //       element: <ReportsUserContentPageComponent />,
        //     },
        //   ],
        // },
        {
          path: 'users',
          element: (
            <RouteGuard permission="crud users" redirectTo="/access-denied">
              <Outlet />
            </RouteGuard>
          ),
          children: [
            {
              path: '',
              element: <UsersListPageComponent />,
            },
            {
              path: 'create/:companyId',
              element: <UsersEditPageComponent />,
            },
            {
              path: 'edit/:id',
              element: <UsersEditPageComponent isEdit />,
            },
          ],
        },
        {
          path: 'employees',
          element: (
            <RouteGuard roles={['admin']} redirectTo="/access-denied">
              <Outlet />
            </RouteGuard>
          ),
          children: [
            {
              path: '',
              element: <EmployeesListPageComponent />,
            },
            {
              path: 'create',
              element: <EmployeesEditPageComponent />,
            },
            {
              path: 'edit/:id',
              element: <EmployeesEditPageComponent isEdit />,
            },
          ],
        },
        {
          path: 'badges',
          element: (
            <RouteGuard roles={['contentmanager', 'admin']} redirectTo="/access-denied">
              <Outlet />
            </RouteGuard>
          ),
          children: [
            {
              path: '',
              element: <BadgesListPageComponent />,
            },
            {
              path: 'create',
              element: <BadgesEditPageComponent />,
            },
            {
              path: 'edit/:id',
              element: <BadgesEditPageComponent isEdit />,
            },
          ],
        },
        {
          path: 'translations',
          element: (
            <RouteGuard roles={['contentmanager', 'admin']} redirectTo="/access-denied">
              <Outlet />
            </RouteGuard>
          ),
          children: [
            {
              path: '',
              element: <TranslationsListPageComponent />,
            },
            {
              path: 'create',
              element: <TranslationsEditPageComponent />,
            },
            {
              path: 'edit/:id',
              element: <TranslationsEditPageComponent isEdit />,
            },
          ],
        },
        {
          path: 'categories',
          element: (
            <RouteGuard roles={['contentmanager', 'admin']} redirectTo="/access-denied">
              <Outlet />
            </RouteGuard>
          ),
          children: [
            {
              path: '',
              element: <CategoriesListPageComponent />,
            },
            {
              path: 'create',
              element: <CategoriesEditPageComponent />,
            },
            {
              path: 'edit/:id',
              element: <CategoriesEditPageComponent isEdit />,
            },
          ],
        },
        {
          path: 'companies',
          element: (
            <RouteGuard permission="crud companies" redirectTo="/access-denied">
              <Outlet />
            </RouteGuard>
          ),
          children: [
            {
              path: '',
              element: <CompaniesListPageComponent />,
            },
            {
              path: 'view/:id',
              element: <CompaniesViewPageComponent />,
            },
          ],
        },
        {
          path: 'albums',
          element: (
            <RouteGuard permission="crud photo" redirectTo="/access-denied">
              <Outlet />
            </RouteGuard>
          ),
          children: [
            {
              path: '',
              element: <AlbumsPageComponent />,
            },
            {
              path: 'upload/:id',
              element: <AlbumsUploadPageComponent />,
            },
            {
              path: 'edit/:id',
              element: <AlbumsPageComponent />,
            },
            {
              path: 'view/:id',
              element: <AlbumsViewPageComponent />,
            },
          ],
        },
        {
          path: 'videos',
          element: (
            <RouteGuard permission="crud photo" redirectTo="/access-denied">
              <Outlet />
            </RouteGuard>
          ),
          children: [
            { path: '', element: <VideosPageComponent /> },
            { path: 'view/:id', element: <VideoViewPageComponent /> },
          ],
        },
        {
          path: 'photos',
          element: (
            <RouteGuard permission="crud photo" redirectTo="/access-denied">
              <Outlet />
            </RouteGuard>
          ),
          children: [
            {
              path: '',
              element: <PhotosPageComponent />,
            },
            {
              path: 'local',
              children: [
                {
                  path: '',
                  element: <PhotosPageComponent />,
                },
                {
                  path: 'upload',
                  element: <PhotosUploadPageComponent />,
                },
                {
                  path: 'edit',
                  element: <PhotosEditPageComponent />,
                },
                {
                  path: 'translate',
                  element: <PhotosTranslatePageComponent />,
                },
              ],
            },
            {
              path: 'foreign',
              children: [
                {
                  path: '',
                  element: <PhotosForeignPageComponent />,
                },
                {
                  path: 'translate',
                  element: <PhotosTranslatePageComponent isForeign />,
                },
              ],
            },
          ],
        },
        {
          path: 'easy-2-read',
          element: (
            <RouteGuard permission="crud easy to read" redirectTo="/access-denied">
              <Outlet />
            </RouteGuard>
          ),
          children: [
            {
              path: 'generator',
              element: <EasyToReadGeneratorPageComponent />,
            },
          ],
        },
      ],
    },
    {
      path: 'reports',
      element: (
        <RouteGuard permissions={['crud reports', 'view activity log']} redirectTo="/access-denied">
          <Outlet />
        </RouteGuard>
      ),
      children: [
        {
          path: 'user-content',
          element: (
            <RouteGuard permission="crud reports" redirectTo="/access-denied">
              <ReportsUserContentPageComponent />
            </RouteGuard>
          ),
        },
        {
          path: 'usage',
          element: (
            <RouteGuard permissions={['crud reports usage']} redirectTo="/access-denied">
              <ReportsUsagePageComponent />
            </RouteGuard>
          ),
        },
        {
          path: 'pressrelease-stats',
          element: (
            <RouteGuard permission="crud reports" roles={['admin', 'manager']} redirectTo="/access-denied">
              <PressreleaseStatsReportPageComponent />
            </RouteGuard>
          ),
        },
        {
          path: 'logs',
          element: (
            <RouteGuard permission="view activity log" redirectTo="/access-denied">
              <ReportsLogsPageComponent />
            </RouteGuard>
          ),
        },
      ],
    },
    {
      path: '/login',
      element: isLoggedIn ? <Navigate to="/" /> : <UsersLoginPageComponent />,
    },
    {
      path: '/access-denied',
      element: <AccessDeniedPageComponent />,
    },
  ]);

  return (
    <div>
      {loading && <CommonAnimatedLoader />}
      {!loading && <LayoutHeadingComponent>{element || <NotFoundPageComponent />}</LayoutHeadingComponent>}
    </div>
  );
}
